import { useEffect, useState } from "react";
import { CalendarCheck2, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const describeApproval = (approval) =>
  approval ? `${approval.days.length === 7 ? "7 days a week" : approval.days.map((d) => DAY_LABELS[d]).join(", ")} · ${approval.start_time}–${approval.end_time}` : "";

export const StandingApprovalPanel = ({ assignment, canEdit, onUpdated }) => {
  const [days, setDays] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const approval = assignment.standing_approval;
  const complete = days.length > 0 && start && end && end > start;

  useEffect(() => {
    setDays(approval?.days || []);
    setStart(approval?.start_time || "");
    setEnd(approval?.end_time || "");
    setNote(approval?.note || "");
    setError("");
  }, [assignment.id, approval]);

  async function save() {
    setBusy(true);
    try {
      const response = await api.put(`/assignments/${assignment.id}/standing-approval`, { days, start_time: start, end_time: end, note });
      onUpdated(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      const response = await api.delete(`/assignments/${assignment.id}/standing-approval`);
      onUpdated(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="assignment-status-panel" data-testid="standing-approval-panel">
      <div className="section-heading"><div><h3><CalendarCheck2 size={17} /> Standing schedule approval</h3><p>Connecteam shifts inside this recurring schedule are not flagged as exceptions. Shifts outside it still are.</p></div></div>
      {approval ? (
        <p className="pairing-meta" data-testid="standing-approval-summary">Approved: {describeApproval(approval)} · by {approval.approved_by_name} on {approval.approved_at?.slice(0, 10)}{approval.note ? ` · ${approval.note}` : ""}</p>
      ) : (
        <p className="pairing-meta" data-testid="standing-approval-empty">No standing approval. Weekend and after-6 p.m. shifts are flagged for CEO review.</p>
      )}
      {canEdit && (
        <div className="standing-approval-form">
          <p className="pairing-meta" data-testid="standing-approval-instructions">Select only the days and hours being approved. Nothing is pre-filled.</p>
          <div className="standing-approval-days">
            {DAY_LABELS.map((label, index) => (
              <label key={label} className="checkbox-row">
                <input type="checkbox" data-testid={`standing-day-${index}`} checked={days.includes(index)} onChange={() => setDays(days.includes(index) ? days.filter((d) => d !== index) : [...days, index].sort())} /> {label}
              </label>
            ))}
          </div>
          <div className="standing-approval-times">
            <label>From <input type="time" data-testid="standing-start-input" value={start} onChange={(e) => setStart(e.target.value)} /></label>
            <label>To <input type="time" data-testid="standing-end-input" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
          </div>
          <input data-testid="standing-note-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="button-row">
            <button type="button" className="button button-primary" data-testid="standing-approval-save" disabled={busy || !complete} onClick={save}>{approval ? "Update approval" : "Approve schedule"}</button>
            {approval && <button type="button" className="button button-ghost" data-testid="standing-approval-remove" disabled={busy} onClick={remove}><Trash2 size={14} /> Remove</button>}
          </div>
          {error && <p className="form-message error-message" data-testid="standing-approval-error">{error}</p>}
        </div>
      )}
    </section>
  );
};
